import PropTypes from 'prop-types';
import { createRef } from 'react';

import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

import classNames from 'classnames';

import ImmutablePropTypes from 'react-immutable-proptypes';
import ImmutablePureComponent from 'react-immutable-pure-component';

import { length } from 'stringz';

import { missingAltTextModal } from 'mastodon/initial_state';

import AutosuggestInput from '../../../components/autosuggest_input';
import AutosuggestTextarea from '../../../components/autosuggest_textarea';
import { Button } from '../../../components/button';
import EmojiPickerDropdown from '../containers/emoji_picker_dropdown_container';
import PollButtonContainer from '../containers/poll_button_container';
import SpoilerButtonContainer from '../containers/spoiler_button_container';
import UploadButtonContainer from '../containers/upload_button_container';
import WarningContainer from '../containers/warning_container';
import { countableText } from '../util/counter';

import { CharacterCounter } from './character_counter';
import { EditIndicator } from './edit_indicator';
import { NavigationBar } from './navigation_bar';
import { PollForm } from "./poll_form";
import { ReplyIndicator } from './reply_indicator';
import { UploadForm } from './upload_form';
import { cancelReplyCompose, changeComposeSpoilerness } from 'mastodon/actions/compose';
import { connect } from 'react-redux';
import { closeCommentForm } from 'mastodon/actions/comment_form';

import { CircularProgress } from '../../../components/circular_progress';

const allowedAroundShortCode = '><\u0085\u0020\u00a0\u1680\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200a\u202f\u205f\u3000\u2028\u2029\u0009\u000a\u000b\u000c\u000d';

const messages = defineMessages({
  placeholder: { id: 'compose_form.placeholder', defaultMessage: 'What is on your mind?' },
  placeholderProfile: { id: 'compose_form.profile.placeholder', defaultMessage: 'How was your day today?' },
  spoiler_placeholder: { id: 'compose_form.spoiler_placeholder', defaultMessage: 'Content warning (optional)' },
  publish: { id: 'compose_form.publish', defaultMessage: 'Post' },
  saveChanges: { id: 'compose_form.save_changes', defaultMessage: 'Update' },
  reply: { id: 'compose_form.reply', defaultMessage: 'Reply' },
});

class ComposeForm extends ImmutablePureComponent {
  static propTypes = {
    intl: PropTypes.object.isRequired,
    text: PropTypes.string.isRequired,
    suggestions: ImmutablePropTypes.list,
    dispatch: PropTypes.func.isRequired,
    spoiler: PropTypes.bool,
    privacy: PropTypes.string,
    spoilerText: PropTypes.string,
    focusDate: PropTypes.instanceOf(Date),
    caretPosition: PropTypes.number,
    preselectDate: PropTypes.instanceOf(Date),
    isSubmitting: PropTypes.bool,
    isChangingUpload: PropTypes.bool,
    isEditing: PropTypes.bool,
    isUploading: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onClearSuggestions: PropTypes.func.isRequired,
    onFetchSuggestions: PropTypes.func.isRequired,
    onSuggestionSelected: PropTypes.func.isRequired,
    onChangeSpoilerText: PropTypes.func.isRequired,
    onPaste: PropTypes.func.isRequired,
    onPickEmoji: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    autoFocus: PropTypes.bool,
    withoutNavigation: PropTypes.bool,
    anyMedia: PropTypes.bool,
    missingAltText: PropTypes.bool,
    isInReply: PropTypes.bool,
    singleColumn: PropTypes.bool,
    lang: PropTypes.string,
    maxChars: PropTypes.number,
    is_reblog: PropTypes.bool,
    isComment: PropTypes.bool,
    formId: PropTypes.string,
  };

  static defaultProps = {
    autoFocus: false,
    onClose: () => {},
  };

  state = {
    highlighted: false,
  };

  constructor(props) {
    super(props);
    this.textareaRef = createRef(null);
    this.initialPrivacyRef = createRef();
    this.initialPrivacyRef.current = props.privacy;
    this.mentionMap = [];
  }

  handleChange = (e) => {
    this.props.onChange(e.target.value);
  };

  handleKeyDown = (e) => {
    if (e.keyCode === 13 && (e.ctrlKey || e.metaKey)) {
      this.handleSubmit();
    }
  };

  getFulltextForCharacterCounting = () => {
    return [this.props.spoiler ? this.props.spoilerText : '', countableText(this.props.text, this.mentionMap)].join('');
  };

  canSubmit = () => {
    const { isSubmitting, isChangingUpload, isUploading, anyMedia, privacy, maxChars } = this.props;
    const fulltext = this.getFulltextForCharacterCounting();
    const isOnlyWhitespace = fulltext.trim().length === 0;

    const hasPrivacyChanged = privacy !== this.initialPrivacyRef.current;

    return hasPrivacyChanged || !(
      isSubmitting ||
      isUploading ||
      isChangingUpload ||
      length(fulltext) > maxChars ||
      (isOnlyWhitespace && !anyMedia) ||
      (!fulltext && !anyMedia)
    );
  };

  handleSubmit = (e) => {
    if (this.props.text !== this.textareaRef.current.value) {
      this.props.onChange(this.textareaRef.current.value);
    }

    if (!this.canSubmit()) {
      return;
    }

    let finalSpoilerText = this.props.spoilerText?.trim();

    if (this.props.spoiler && !finalSpoilerText) {
      finalSpoilerText = this.props.text.trim();
    }

    this.props.onSubmit({
      spoiler: this.props.spoiler || false,
      spoilerText: finalSpoilerText || '',
      isComment: this.props.isComment || false,
      composeOriginId: window.location.pathname.split("/").pop()
    });

    if (!this.props.isEditing) {
      this.props.onClose();

      this.props.dispatch(cancelReplyCompose());
      this.props.dispatch(closeCommentForm());
    }

    if (e) {
      e.preventDefault();
    }
  };

  onSuggestionsClearRequested = () => {
    this.props.onClearSuggestions();
  };

  onSuggestionsFetchRequested = (token) => {
    this.props.onFetchSuggestions(token);
  };

  onSuggestionSelected = (tokenStart, token, value) => {
    this.props.onSuggestionSelected(tokenStart, token, value, ['text']);
    if (value && value.type === 'account' && value.id) {
      let account = this.props.accounts.get(value.id);
      if (account) {
        this.mentionMap.push({
          display: account.get('display_name'),
          username: account.get('acct') || account.get('username'),
        });
      }
    } 
  };

  onSpoilerSuggestionSelected = (tokenStart, token, value) => {
    this.props.onSuggestionSelected(tokenStart, token, value, ['spoiler_text']);
  };

  handleChangeSpoilerText = (e) => {
    this.props.onChangeSpoilerText(e.target.value);
  };

  handleFocus = () => {
    if (this.composeForm && !this.props.singleColumn) {
      const { left, right } = this.composeForm.getBoundingClientRect();
      if (left < 0 || right > (window.innerWidth || document.documentElement.clientWidth)) {
        this.composeForm.scrollIntoView();
      }
    }
  };

  componentDidMount() {
    this._updateFocusAndSelection({});
    if (!this.isIOS()) {
      document.body.style.overflowY = 'hidden';
    }
  }
  
  componentWillUnmount() {
    if (this.timeout) clearTimeout(this.timeout);
    if (!this.isIOS()) {
      document.body.style.overflowY = 'auto';
    }
  }
  
  componentDidUpdate(prevProps) {
    this._updateFocusAndSelection(prevProps);

    if (prevProps.isSubmitting && !this.props.isSubmitting && prevProps.isEditing) {
      this.props.onClose();
      this.props.dispatch(cancelReplyCompose());
      this.props.dispatch(closeCommentForm());
    }
  }
  
  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }

  _updateFocusAndSelection = (prevProps) => {
    if (this.props.focusDate && this.props.focusDate !== prevProps.focusDate) {
      let selectionEnd, selectionStart;

      if (this.props.preselectDate !== prevProps.preselectDate && this.props.isInReply) {
        selectionEnd = this.props.text.length;
        selectionStart = this.props.text.search(/\s/) + 1;
      } else if (typeof this.props.caretPosition === 'number') {
        selectionStart = this.props.caretPosition;
        selectionEnd = this.props.caretPosition;
      } else {
        selectionEnd = this.props.text.length;
        selectionStart = selectionEnd;
      }

      Promise.resolve().then(() => {
        this.textareaRef.current.setSelectionRange(selectionStart, selectionEnd);
        this.textareaRef.current.focus();
        this.setState({ highlighted: true });
        this.timeout = setTimeout(() => this.setState({ highlighted: false }), 700);
      }).catch(console.error);
    } else if (prevProps.isSubmitting && !this.props.isSubmitting) {
      this.textareaRef.current.focus();
    } else if (this.props.spoiler !== prevProps.spoiler) {
      if (this.props.spoiler) {
        this.spoilerText.input.focus();
      } else if (prevProps.spoiler) {
        this.textareaRef.current.focus();
      }
    }
  };

  setSpoilerText = (c) => {
    this.spoilerText = c;
  };

  setRef = c => {
    this.composeForm = c;
  };

  handleEmojiPick = (data) => {
    const { text } = this.props;
    const position = this.textareaRef.current.selectionStart;
    const needsSpace = data.custom && position > 0 && !allowedAroundShortCode.includes(text[position - 1]);

    this.props.onPickEmoji(position, data, needsSpace);
  };

  getPlaceholder = () => {
    const { intl } = this.props;
    const isProfilePage = window.location.pathname.match(/^\/@[\w]+$/);
    return intl.formatMessage(isProfilePage ? messages.placeholderProfile : messages.placeholder);
  };

  handleComposeSpoilerness = () => {
    this.props.dispatch(changeComposeSpoilerness());
  };

  render() {
    const { intl, onPaste, autoFocus, withoutNavigation, maxChars, isSubmitting, isEditing } = this.props;
    const { highlighted } = this.state;
    const disabled = isSubmitting;

    return (
      <form id={this.props.formId} className='compose-form' onSubmit={this.handleSubmit}>
        {/* <ReplyIndicator /> */}
        {!withoutNavigation && <NavigationBar />}
        <WarningContainer />

        <div className={classNames('compose-form__highlightable', { active: highlighted })} ref={this.setRef}>
          <div className='compose-form__scrollable'>
            {/* <EditIndicator /> */}

            {this.props.spoiler && (
              <div className='spoiler-input'>
                <div className='spoiler-input__border' />

                <AutosuggestInput
                  placeholder={this.getPlaceholder()}
                  value={this.props.spoilerText}
                  disabled={disabled}
                  onChange={this.handleChangeSpoilerText}
                  onKeyDown={this.handleKeyDown}
                  ref={this.setSpoilerText}
                  suggestions={this.props.suggestions}
                  onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                  onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                  onSuggestionSelected={this.onSpoilerSuggestionSelected}
                  searchTokens={[':']}
                  id='cw-spoiler-input'
                  className='spoiler-input__input'
                  lang={this.props.lang}
                  spellCheck
                />

                <div className='spoiler-input__border' />
              </div>
            )}

            <AutosuggestTextarea
              ref={this.textareaRef}
              placeholder={this.getPlaceholder()}
              disabled={disabled}
              value={this.props.text}
              onChange={this.handleChange}
              suggestions={this.props.suggestions}
              onFocus={this.handleFocus}
              onKeyDown={this.handleKeyDown}
              onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
              onSuggestionsClearRequested={this.onSuggestionsClearRequested}
              onSuggestionSelected={this.onSuggestionSelected}
              onPaste={onPaste}
              autoFocus={autoFocus}
              lang={this.props.lang}
            />
          </div>

          {!this.props.is_reblog && (
            <>
              <UploadForm />
              <PollForm />

              <div className='compose-form__footer'>
                <div className='compose-form__actions'>
                  <div className='compose-form__buttons'>
                    <UploadButtonContainer usageType='create' isComposeForm />
                    <PollButtonContainer isComposeForm disabled={this.props.anyMedia} />
                    <div className='compose-form___action-full' onClick={this.handleComposeSpoilerness}>
                      <SpoilerButtonContainer isComposeForm />
                      <FormattedMessage
                        id='compose.add_warning'
                        defaultMessage='Add content warning'
                      />
                    </div>
                    {/* <EmojiPickerDropdown isComposeForm onPickEmoji={this.handleEmojiPick} /> */}
                    <CharacterCounter isComposeForm max={maxChars} text={this.getFulltextForCharacterCounting()} />
                  </div>
                </div>
              </div>
            </>
          )}

        </div>
        <div className='compose-form__submit'>
          <Button
            type='submit'
            compact
            className='button-submit-full'
            text={intl.formatMessage(isEditing ? messages.saveChanges :  messages.publish)}
            disabled={!this.canSubmit()}
            prefixIcon={(isSubmitting && isEditing) ? <CircularProgress size={16} strokeWidth={2} /> : undefined}
          />
        </div>
      </form>
    );
  }

}

export default injectIntl(connect()(ComposeForm));
